import type { CtInterviewSubmissionSummary } from '../../api/types';

export type SortKey = 'candidateName' | 'location' | 'submittedAtUtc' | 'answeredCount' | 'correctCount';

export interface SubmissionTableProps {
  submissions: CtInterviewSubmissionSummary[];
  emptyMessage: string;

  sortKey: SortKey;
  sortDescending: boolean;
  onSort: (key: SortKey) => void;

  onOpen: (submission: CtInterviewSubmissionSummary) => void;
}

/**
 * The dashboard's table.
 *
 * Reuses the `care-table` styles the complimentary care dashboard introduced rather than cloning
 * them under a second name — they are review-dashboard styles that happen to have been written for
 * the first review dashboard, and two copies would drift the moment either page is touched.
 */
export function SubmissionTable({
  submissions,
  emptyMessage,
  sortKey,
  sortDescending,
  onSort,
  onOpen,
}: SubmissionTableProps) {
  return (
    <div className="care-table__scroll">
      <table className="care-table care-table--fixed">
        <colgroup>
          <col className="care-col--employee" />
          <col className="care-col--enrollees" />
          <col className="care-col--date" />
          <col className="care-col--status" />
          <col className="care-col--score" />
        </colgroup>
        <thead>
          <tr>
            <SortableHeader label="Candidate" columnKey="candidateName" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <SortableHeader label="Location" columnKey="location" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <SortableHeader label="Handed in" columnKey="submittedAtUtc" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <SortableHeader label="Answered" columnKey="answeredCount" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <SortableHeader label="Score" columnKey="correctCount" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
          </tr>
        </thead>
        <tbody>
          {submissions.map((submission) => (
            <SubmissionRow key={submission.id} submission={submission} onOpen={onOpen} />
          ))}
        </tbody>
      </table>

      {submissions.length === 0 && <p className="care-empty">{emptyMessage}</p>}
    </div>
  );
}

function SubmissionRow({
  submission,
  onOpen,
}: {
  submission: CtInterviewSubmissionSummary;
  onOpen: (submission: CtInterviewSubmissionSummary) => void;
}) {
  const complete = submission.answeredCount === submission.questionCount;
  const submitted = new Date(submission.submittedAtUtc);

  return (
    <tr
      className="care-table__row"
      onClick={() => onOpen(submission)}
      tabIndex={0}
      role="button"
      aria-label={`Open ${submission.candidateName}'s assessment`}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(submission);
        }
      }}
    >
      <td>
        <div className="care-table__primary">{submission.candidateName}</div>
      </td>
      <td>
        <div className="care-table__primary care-table__ellipsis" title={submission.location}>
          {submission.location}
        </div>
      </td>
      <td>
        <div className="care-table__primary">{submitted.toLocaleDateString()}</div>
        {/* The time matters here in a way a date alone does not: assessments are sat back to back
            during an interview day, so several share a date and only the clock orders them. */}
        <div className="care-table__muted">
          {submitted.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </div>
      </td>
      <td>
        {/* Beside the count, not under it: the tag qualifies the number, and stacking it made
            every incomplete row taller than its neighbours for no reason.
            Only the incomplete ones say anything — a tag on every complete row would be noise on
            what should be the normal case. Amber rather than red: an assessment someone did not
            finish is worth noticing, not an error. */}
        <div className="interview-answered">
          <span className="care-table__primary">
            {submission.answeredCount} / {submission.questionCount}
          </span>
          {!complete && <span className="care-tag care-tag--flush">Incomplete</span>}
        </div>
      </td>
      <td>
        {/* Only the multiple-choice sections are marked, so this is a count out of those rather than
            out of the whole assessment — which is also why it is a second column and not a tag on
            the one beside it. An assessment sat before the questions had options has nothing marked
            at all; a dash says so, where "0 / 0" would read like a candidate who got everything
            wrong. */}
        {submission.scoredCount === 0 ? (
          <div className="care-table__muted">Not scored</div>
        ) : (
          <div className="care-table__primary">
            {submission.correctCount} / {submission.scoredCount}
          </div>
        )}
      </td>
    </tr>
  );
}

interface SortableHeaderProps {
  label: string;
  columnKey: SortKey;
  activeKey: SortKey;
  descending: boolean;
  onSort: (key: SortKey) => void;
}

function SortableHeader({ label, columnKey, activeKey, descending, onSort }: SortableHeaderProps) {
  const isActive = activeKey === columnKey;

  return (
    <th aria-sort={isActive ? (descending ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="care-table__sort" onClick={() => onSort(columnKey)}>
        {label}
        <span className="care-table__sort-arrow" aria-hidden="true">
          {isActive ? (descending ? '▾' : '▴') : ''}
        </span>
      </button>
    </th>
  );
}
